import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
  managerId?: string;
  displayName?: string;
  title?: string;
  businessPhone?: string;
  mobilePhone?: string;
  status: string;
  active: boolean;
  lockedOut: boolean;
}

interface ManagerOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string | null;
}

interface RoleOption {
  id: string;
  name: string;
  roleType: string;
  description?: string;
}

const jobTitleOptions = [
  { value: 'Manager', label: 'Manager' },
  { value: 'Team Lead', label: 'Team Lead' },
  { value: 'Team Member', label: 'Team Member' }
];

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' }
];

const UserCreate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const usersListSearch = (location.state as { usersListSearch?: string } | null)?.usersListSearch;
  const usersListPath =
    usersListSearch !== undefined ? `/users${usersListSearch}` : '/users';
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UserFormData>({
    firstName: '',
    lastName: '',
    email: '',
    roleId: '',
    managerId: '',
    displayName: '',
    title: '',
    businessPhone: '',
    mobilePhone: '',
    status: 'active',
    active: true,
    lockedOut: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Search query states for comboboxes
  const [roleQuery, setRoleQuery] = useState('');
  const [managerQuery, setManagerQuery] = useState('');
  const [titleQuery, setTitleQuery] = useState('');
  const [statusQuery, setStatusQuery] = useState('');

  // Get all available roles (system + custom) - no limit
  const { data: rolesData } = useQuery({
    queryKey: ['roles-all'],
    queryFn: async () => {
      const response = await api.get('/roles', {
        params: { limit: 1000, active: true }
      });
      return response.data;
    },
  });

  // Get all users who can be managers (active users with manager/admin roles)
  const { data: managersData } = useQuery({
    queryKey: ['potentialManagers'],
    queryFn: async () => {
      const response = await api.get('/users', {
        params: { limit: 1000, active: true }
      });
      return response.data;
    },
  });

  // Update the default role when roles are loaded
  useEffect(() => {
    if (rolesData?.roles && rolesData.roles.length > 0 && !formData.roleId) {
      // Find the 'user' role as default, or fall back to first available
      const userRole = availableRoles.find((r: RoleOption) => r.name === 'user');
      setFormData(prev => ({
        ...prev,
        roleId: userRole?.id || rolesData.roles[0].id
      }));
    }
  }, [rolesData]);

  const createUserMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const response = await api.post('/users/create-by-admin', data);
      return response.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });

      toast.success(
        <div>
          <p>User created successfully!</p>
          <p className="text-sm mt-1">Default password: <code className="bg-gray-100 px-1 py-0.5 rounded">Password123!</code></p>
          <p className="text-xs mt-1 text-gray-600">User will be prompted to change password on first login</p>
        </div>,
        { duration: 8000 }
      );
      navigate(usersListPath);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create user';
      toast.error(message);

      if (error.response?.status === 409) {
        setErrors({ email: 'A user with this email already exists' });
      }
    }
  });

  const validateField = (name: string, value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

    switch (name) {
      case 'email':
        if (value && !emailRegex.test(value)) {
          return 'Invalid email format';
        }
        break;
      case 'businessPhone':
      case 'mobilePhone':
        if (value) {
          const cleanedPhone = value.replace(/\s+/g, '');
          if (!phoneRegex.test(cleanedPhone)) {
            return 'Invalid phone number format';
          }
          const digitCount = cleanedPhone.replace(/[^\d]/g, '').length;
          if (digitCount < 10) {
            return 'Phone number must have at least 10 digits';
          }
        }
        break;
    }
    return '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (errors.email || errors.businessPhone || errors.mobilePhone) {
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createUserMutation.mutate(formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Filter to show only SYSTEM and CUSTOM roles (exclude GOD and CUSTOMER)
  const availableRoles: RoleOption[] = (rolesData?.roles || []).filter((role: any) =>
    role.roleType === 'SYSTEM' || role.roleType === 'CUSTOM'
  );

  // Filter managers (users with Manager or Team Lead title)
  const availableManagers: ManagerOption[] = (managersData?.users || [])
    .filter((user: ManagerOption) => user.title === 'Manager' || user.title === 'Team Lead');

  // Get display name for selected role
  const getSelectedRole = () => {
    return availableRoles.find((r: RoleOption) => r.id === formData.roleId);
  };

  // Get display name for selected manager
  const getSelectedManager = () => {
    return availableManagers.find((m: ManagerOption) => m.id === formData.managerId);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(usersListPath)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Users
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create New User</h1>
            <p className="text-sm text-gray-600 mt-1">
              New users will be created with the default password: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">Password123!</code>
            </p>
            <p className="text-sm text-gray-600">
              They will be required to change their password on first login.
            </p>
            {availableRoles.length > 0 && (
              <p className="text-sm text-blue-600 mt-2">
                {availableRoles.filter((r: RoleOption) => r.roleType === 'SYSTEM').length} system role(s) and {availableRoles.filter((r: RoleOption) => r.roleType === 'CUSTOM').length} custom role(s) available
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: First Name and Last Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.firstName
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.lastName
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Display Name */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                      Display Name
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Job Title and Email Address */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.title || ''}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, title: value || '' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(value: string) => {
                            const option = jobTitleOptions.find(o => o.value === value);
                            return option ? option.label : '';
                          }}
                          onChange={(e) => setTitleQuery(e.target.value)}
                          placeholder="Select Job Title"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setTitleQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {jobTitleOptions
                              .filter(option => option.label.toLowerCase().includes(titleQuery.toLowerCase()))
                              .map((option) => (
                                <Combobox.Option
                                  key={option.value}
                                  value={option.value}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {option.label}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-3">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.email
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Email must be unique. This will be used as the username for login.
                    </p>
                  </div>
                </div>
              </div>
            </div>


            {/* Row 4: Business Phone and Mobile Phone */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Business Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      id="businessPhone"
                      name="businessPhone"
                      value={formData.businessPhone}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.businessPhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.businessPhone && (
                      <p className="mt-1 text-xs text-red-600">{errors.businessPhone}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="mobilePhone" className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Phone
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="tel"
                      id="mobilePhone"
                      name="mobilePhone"
                      value={formData.mobilePhone}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.mobilePhone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.mobilePhone && (
                      <p className="mt-1 text-xs text-red-600">{errors.mobilePhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Role and Manager */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.roleId}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, roleId: value || '' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(roleId: string) => {
                            const role = availableRoles.find((r: RoleOption) => r.id === roleId);
                            if (!role) return '';
                            const displayName = role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ');
                            return role.roleType === 'CUSTOM' ? `${displayName} ⭐` : displayName;
                          }}
                          onChange={(e) => setRoleQuery(e.target.value)}
                          placeholder="Search roles..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setRoleQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {availableRoles.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No roles found.
                              </div>
                            ) : (
                              availableRoles
                                .filter((role: RoleOption) => {
                                  const displayName = role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ');
                                  return displayName.toLowerCase().includes(roleQuery.toLowerCase()) ||
                                         (role.description && role.description.toLowerCase().includes(roleQuery.toLowerCase()));
                                })
                                .map((role: RoleOption) => (
                                  <Combobox.Option
                                    key={role.id}
                                    value={role.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {role.name.charAt(0).toUpperCase() + role.name.slice(1).replace('_', ' ')}
                                          {role.roleType === 'CUSTOM' && ' ⭐'}
                                        </span>
                                        {role.description && (
                                          <span className={`block truncate text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                            {role.description}
                                          </span>
                                        )}
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}>
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {getSelectedRole()?.roleType === 'CUSTOM' && (
                      <p className="mt-1 text-xs text-purple-600">⭐ Custom role with specific permissions</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="managerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Manager
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.managerId || ''}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, managerId: value || '' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(managerId: string) => {
                            if (!managerId) return '';
                            const manager = availableManagers.find((m: ManagerOption) => m.id === managerId);
                            return manager ? `${manager.firstName} ${manager.lastName}` : '';
                          }}
                          onChange={(e) => setManagerQuery(e.target.value)}
                          placeholder="Search managers..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setManagerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {/* No Manager option */}
                            <Combobox.Option
                              value=""
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                }`
                              }
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'} italic`}>
                                    No Manager
                                  </span>
                                  {selected && (
                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? 'text-white' : 'text-indigo-600'
                                    }`}>
                                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                            {availableManagers
                              .filter((manager: ManagerOption) => {
                                const searchStr = `${manager.firstName} ${manager.lastName} ${manager.email}`.toLowerCase();
                                return searchStr.includes(managerQuery.toLowerCase());
                              })
                              .map((manager: ManagerOption) => (
                                <Combobox.Option
                                  key={manager.id}
                                  value={manager.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {manager.firstName} {manager.lastName}
                                      </span>
                                      <span className={`block truncate text-xs ${active ? 'text-indigo-200' : 'text-gray-500'}`}>
                                        {manager.email} {manager.title && `• ${manager.title}`}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    <p className="mt-1 text-xs text-gray-500">
                      Select the reporting manager for this user
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Status */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.status}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, status: value || 'active' }));
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                          displayValue={(value: string) => {
                            const option = statusOptions.find(o => o.value === value);
                            return option ? option.label : '';
                          }}
                          onChange={(e) => setStatusQuery(e.target.value)}
                          placeholder="Select Status"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setStatusQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {statusOptions
                              .filter(option => option.label.toLowerCase().includes(statusQuery.toLowerCase()))
                              .map((option) => (
                                <Combobox.Option
                                  key={option.value}
                                  value={option.value}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {option.label}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              <div className="col-span-6"></div>
            </div>

            {/* Row 6: Active and Locked Out checkboxes */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="active"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="active" className="font-medium text-gray-700">
                      Active
                    </label>
                    <p className="text-gray-500">User can access the system</p>
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      id="lockedOut"
                      name="lockedOut"
                      checked={formData.lockedOut}
                      onChange={handleInputChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="lockedOut" className="font-medium text-gray-700">
                      Locked Out
                    </label>
                    <p className="text-gray-500">User is locked out of the system</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/users')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createUserMutation.isPending}
                className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createUserMutation.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default UserCreate;
